import { useState } from "react"
import { ChevronRight, type LucideIcon } from "lucide-react"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "./ui/sidebar"
import { cn } from "../lib/utils"

export interface NavLeaf {
  title: string
  url?: string
  disabled?: boolean
  badge?: string
  isActive?: boolean
  onClick?: () => void
}

export interface NavGroup {
  label: string
  items: NavLeaf[]
}

export interface NavItem {
  title: string
  url?: string
  icon?: LucideIcon
  isActive?: boolean
  groups?: NavGroup[]
}

export function NavMain({
  items,
  onNavigate,
}: {
  items: NavItem[]
  onNavigate?: (url: string) => void
}) {
  // Track expansion per-item. Default expanded for items that are active so
  // the sub-tree is visible when you land on a relevant page.
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(items.filter((i) => i.isActive && i.groups).map((i) => [i.title, true]))
  )

  const toggle = (title: string) =>
    setExpanded((prev) => ({ ...prev, [title]: !prev[title] }))

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          {items.map((item) => {
            const hasGroups = !!item.groups?.length
            const isOpen = expanded[item.title] ?? false

            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  tooltip={item.title}
                  isActive={item.isActive}
                  onClick={() => {
                    if (hasGroups) {
                      toggle(item.title)
                    }
                    if (item.url && onNavigate) {
                      onNavigate(item.url)
                    }
                  }}
                >
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                  {hasGroups && (
                    <ChevronRight
                      className={cn(
                        "ml-auto h-4 w-4 transition-transform duration-200",
                        isOpen && "rotate-90"
                      )}
                    />
                  )}
                </SidebarMenuButton>

                {hasGroups && isOpen && (
                  <div className="mt-1">
                    {item.groups!.map((group) => (
                      <div key={group.label} className="mb-1">
                        <p className="px-5 pb-1 pt-2 text-[10px] font-medium uppercase tracking-[0.12em] text-sidebar-foreground/50">
                          {group.label}
                        </p>
                        <SidebarMenuSub>
                          {group.items.map((leaf) => (
                            <SidebarMenuSubItem key={leaf.title}>
                              <SidebarMenuSubButton
                                isActive={leaf.isActive}
                                aria-disabled={leaf.disabled || undefined}
                                onClick={(e) => {
                                  if (leaf.disabled) {
                                    e.preventDefault()
                                    return
                                  }
                                  if (leaf.onClick) {
                                    leaf.onClick()
                                    return
                                  }
                                  if (leaf.url && onNavigate) onNavigate(leaf.url)
                                }}
                                className={cn(
                                  leaf.disabled && "cursor-not-allowed opacity-50"
                                )}
                              >
                                <span className="truncate">{leaf.title}</span>
                                {leaf.badge && (
                                  <span className="ml-auto rounded-full border border-sidebar-border bg-sidebar-accent/50 px-1.5 py-0 text-[9px] font-medium uppercase tracking-wide text-sidebar-foreground/60">
                                    {leaf.badge}
                                  </span>
                                )}
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </div>
                    ))}
                  </div>
                )}
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}